import { createContext, useContext, useEffect, useState } from "react";
import { API_STATUS } from "app/shared/constants";
import { LOADER_TYPES } from "./constants";
import {
  initLoading,
  initOISSearchFilters,
  initOISTableFilterOptions,
  initUploadEnvironment,
  initUploadEnvironments,
} from "./initialStates";
import OISApi from "./OISApi";

const OISContext = createContext();

export const OISProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(initLoading);
  const [searchFilters, setSearchFilters] = useState(initOISSearchFilters);
  const [selectedEnvironment, setSelectedEnvironment] = useState(
    initUploadEnvironment,
  );
  const [filterOptions, setFilterOptions] = useState(initOISTableFilterOptions);
  const [uploadEnvironments, setUploadEnvironments] = useState(
    initUploadEnvironments,
  );
  const [eProtocolRecords, setEProtocolRecords] = useState([]);
  const [recentActivityRecords, setRecentActivityRecords] = useState([]);
  const [uploadLink, setUploadLink] = useState(null);
  const [shouldPollRecentActivity, setShouldPollRecentActivity] =
    useState(true);
  const api = OISApi();

  // Pseudo-reducer to keep loading states updated
  const setLoader = (type, isSet) => {
    setIsLoading((prev) => ({
      ...prev,
      [type]: isSet,
    }));
  };

  // Update a filter value
  const updateFilter = (field, value) => {
    setSearchFilters((prev) => {
      const newState = { ...prev, [field]: value };
      return newState;
    });
  };

  /**
   * Async API Calls
   */

  const getFilterOptions = async () => {
    setLoader(LOADER_TYPES.OIS_METADATA, true);

    const metaData = await api.getOISMetadata();

    if (metaData === API_STATUS.API_ERROR) {
      setLoader(LOADER_TYPES.OIS_METADATA, false);
      return;
    }

    setFilterOptions((prev) => ({
      ...prev,
      ...metaData,
    }));

    setLoader(LOADER_TYPES.OIS_METADATA, false);
  };

  const getUploadEnvironments = async () => {
    setLoader(LOADER_TYPES.UPLOAD_ENVIRONMENTS, true);

    const metaData = await api.getUploadEnvironments();

    if (metaData === API_STATUS.API_ERROR) {
      setLoader(LOADER_TYPES.UPLOAD_ENVIRONMENTS, false);
      return;
    }

    setUploadEnvironments((prev) => ({
      ...prev,
      ...metaData,
    }));

    setLoader(LOADER_TYPES.OIS_METADATA, false);
  };

  /**
   * Fetch Recent Activity Records
   */
  const fetchRecentActivity = async () => {
    if (recentActivityRecords.length === 0) {
      setLoader(LOADER_TYPES.OIS_DATA, true);
    }

    const data = await api.getOISRecentActivity();

    if (data === API_STATUS.API_ERROR) {
      setLoader(LOADER_TYPES.OIS_DATA, false);
      return;
    }

    if (Array.isArray(data)) {
      setRecentActivityRecords(data);

      const allFinished = data.every(
        (row) => row.status === "finished" || row.status === "failed",
      );
      setShouldPollRecentActivity(!allFinished);
    }

    setLoader(LOADER_TYPES.OIS_DATA, false);
  };

  /**
   * Search eProtocol Activity, updates eProtocolRecords state
   */
  const searchEProtocol = async (startDate, endDate) => {
    setLoader(LOADER_TYPES.EPROTOCOL_ACTIVITY, true);
    setEProtocolRecords([]);

    const data = await api.getEProtocolActivity(startDate, endDate);

    if (data === API_STATUS.API_ERROR) {
      setLoader(LOADER_TYPES.EPROTOCOL_ACTIVITY, false);
      return;
    }

    if (data) {
      setEProtocolRecords(data);
      if (data._links?.upload?.href) {
        setUploadLink(data._links.upload.href);
      }
    }

    setLoader(LOADER_TYPES.EPROTOCOL_ACTIVITY, false);
  };

  /**
   * Upload file to OnCore
   */
  const uploadToOnCore = async (csvType, instance, uploadLink, formData) => {
    setLoader(LOADER_TYPES.UPLOAD_TO_ONCORE, true);

    const status = await api.uploadToOnCore(
      csvType,
      instance,
      uploadLink,
      formData,
    );

    if (status === API_STATUS.API_ERROR) {
      setLoader(LOADER_TYPES.UPLOAD_TO_ONCORE, false);
      return status;
    }

    if (status === API_STATUS.API_SUCCESS) {
      setShouldPollRecentActivity(true);
      fetchRecentActivity();
    }

    setLoader(LOADER_TYPES.UPLOAD_TO_ONCORE, false);
    return status;
  };

  /**
   * Download File (e.g., Export EProtocol)
   */
  const downloadFile = async (link) => {
    setLoader(LOADER_TYPES.DOWNLOAD_DATA, true);

    const status = await api.downloadFile(link);

    if (status === API_STATUS.API_ERROR) {
      setLoader(LOADER_TYPES.DOWNLOAD_DATA, false);
      return;
    }

    setLoader(LOADER_TYPES.DOWNLOAD_DATA, false);
  };

  /********************
   *    useEffects    *
   ********************/

  // Load Metadata
  useEffect(() => {
    getFilterOptions();
    getUploadEnvironments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll for recent activity
  useEffect(() => {
    let intervalId;
    if (shouldPollRecentActivity) {
      fetchRecentActivity();
      intervalId = setInterval(fetchRecentActivity, 20000);
    }
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldPollRecentActivity]);

  const value = {
    isLoading,
    searchFilters,
    filterOptions,
    uploadEnvironments,
    updateFilter,
    selectedEnvironment,
    setSelectedEnvironment,
    eProtocolRecords,
    recentActivityRecords,
    uploadLink,
    searchEProtocol,
    uploadToOnCore,
    fetchRecentActivity,
    downloadFile,
    setShouldPollRecentActivity,
  };

  return <OISContext.Provider value={value}>{children}</OISContext.Provider>;
};

export const useOIS = () => {
  const context = useContext(OISContext);
  if (!context) {
    throw new Error("useOIS must be used within an OISProvider");
  }
  return context;
};
